import * as THREE from 'three';

export const LUT_SIZE = 32;

// The values are deliberately restrained. The grade has to make the arena
// world legible without repainting the warm, moderately saturated hues where
// the card portraits' skin lives.
export const LUT_GRADES = Object.freeze({
  IRON: Object.freeze({
    temperature: -0.34, tint: -0.02, lift: -0.018, gamma: 0.91, gain: 1.06,
    saturation: 0.82,
    splitTone: Object.freeze({ shadowHue: 218, highlightHue: 200, shadowAmount: 0.075, highlightAmount: 0.025 }),
  }),
  VOYAGE: Object.freeze({
    temperature: 0.05, tint: -0.06, lift: 0.002, gamma: 0.98, gain: 1.02,
    saturation: 0.94,
    splitTone: Object.freeze({ shadowHue: 184, highlightHue: 38, shadowAmount: 0.085, highlightAmount: 0.060 }),
  }),
  HUSTLE: Object.freeze({
    temperature: 0.38, tint: 0.00, lift: -0.006, gamma: 1.00, gain: 1.04,
    saturation: 1.12,
    splitTone: Object.freeze({ shadowHue: 18, highlightHue: 42, shadowAmount: 0.045, highlightAmount: 0.075 }),
  }),
  MYSTIC: Object.freeze({
    temperature: -0.12, tint: 0.22, lift: 0.025, gamma: 1.06, gain: 0.99,
    saturation: 0.96,
    splitTone: Object.freeze({ shadowHue: 286, highlightHue: 252, shadowAmount: 0.080, highlightAmount: 0.040 }),
  }),
  CHAOS: Object.freeze({
    temperature: 0.02, tint: -0.20, lift: -0.030, gamma: 0.87, gain: 1.10,
    saturation: 1.08,
    splitTone: Object.freeze({ shadowHue: 154, highlightHue: 72, shadowAmount: 0.045, highlightAmount: 0.100 }),
  }),
  HEART: Object.freeze({
    temperature: 0.24, tint: 0.16, lift: 0.014, gamma: 1.05, gain: 1.00,
    saturation: 1.02,
    splitTone: Object.freeze({ shadowHue: 338, highlightHue: 24, shadowAmount: 0.055, highlightAmount: 0.055 }),
  }),
});

const cache = new Map();

function srgbToLinear(x) {
  return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(x) {
  const v = Math.min(1, Math.max(0, x));
  return v <= 0.0031308 ? v * 12.92 : 1.055 * (v ** (1 / 2.4)) - 0.055;
}

function hueVector(degrees) {
  const h = ((degrees % 360) + 360) % 360 / 60;
  const x = 1 - Math.abs((h % 2) - 1);
  const rgb = h < 1 ? [1, x, 0] : h < 2 ? [x, 1, 0] : h < 3 ? [0, 1, x]
    : h < 4 ? [0, x, 1] : h < 5 ? [x, 0, 1] : [1, 0, x];
  const mean = (rgb[0] + rgb[1] + rgb[2]) / 3;
  return rgb.map((channel) => channel - mean);
}

function rgbHueSat(r, g, b) {
  const hi = Math.max(r, g, b), lo = Math.min(r, g, b);
  const d = hi - lo;
  let hue = 0;
  if (d > 1e-6) {
    if (hi === r) hue = 60 * (((g - b) / d) % 6);
    else if (hi === g) hue = 60 * ((b - r) / d + 2);
    else hue = 60 * ((r - g) / d + 4);
  }
  if (hue < 0) hue += 360;
  return { hue, saturation: hi > 1e-6 ? d / hi : 0, value: hi };
}

function hsvToRgb(hue, saturation, value) {
  const h = ((hue % 360) + 360) % 360 / 60;
  const c = value * saturation;
  const x = c * (1 - Math.abs((h % 2) - 1));
  const rgb = h < 1 ? [c, x, 0] : h < 2 ? [x, c, 0] : h < 3 ? [0, c, x]
    : h < 4 ? [0, x, c] : h < 5 ? [x, 0, c] : [c, 0, x];
  const m = value - c;
  return rgb.map((channel) => channel + m);
}

function gradeRgb(input, grade) {
  let r = srgbToLinear(input[0]);
  let g = srgbToLinear(input[1]);
  let b = srgbToLinear(input[2]);

  // A compact temperature/tint balance. Tint is intentionally weaker than
  // temperature: magenta/green casts are the fastest way to make a face ill.
  r *= 1 + grade.temperature * 0.20 + grade.tint * 0.045;
  g *= 1 - grade.tint * 0.12;
  b *= 1 - grade.temperature * 0.20 + grade.tint * 0.035;

  const exponent = 1 / Math.max(0.1, grade.gamma);
  r = Math.max(0, ((r + grade.lift) * grade.gain)) ** exponent;
  g = Math.max(0, ((g + grade.lift) * grade.gain)) ** exponent;
  b = Math.max(0, ((b + grade.lift) * grade.gain)) ** exponent;

  const luma = r * 0.2126 + g * 0.7152 + b * 0.0722;
  r = luma + (r - luma) * grade.saturation;
  g = luma + (g - luma) * grade.saturation;
  b = luma + (b - luma) * grade.saturation;

  const shadow = (1 - Math.min(1, luma)) ** 1.65;
  const highlight = Math.min(1, luma) ** 1.45;
  const shadowTone = hueVector(grade.splitTone.shadowHue);
  const highlightTone = hueVector(grade.splitTone.highlightHue);
  for (let channel = 0; channel < 3; channel++) {
    const tone = (shadowTone[channel] * shadow * grade.splitTone.shadowAmount
      + highlightTone[channel] * highlight * grade.splitTone.highlightAmount) * 0.45;
    if (channel === 0) r += tone;
    else if (channel === 1) g += tone;
    else b += tone;
  }

  let out = [linearToSrgb(r), linearToSrgb(g), linearToSrgb(b)];

  // Protect warm portrait colours. This is based on the LUT input, so it also
  // covers artwork without requiring a face mask or another texture lookup.
  const skin = rgbHueSat(input[0], input[1], input[2]);
  // Include the red wraparound and a generous amber edge so trilinear samples
  // around protected colours never interpolate against an unsafe neighbour.
  const warmHue = skin.hue >= 330 || skin.hue <= 90;
  const warmSat = skin.saturation >= 0.04 && skin.saturation <= 0.90;
  const value = Math.max(...input);
  if (warmHue && warmSat && value >= 0.04) {
    const protect = 0.68;
    out = out.map((channel, i) => channel * (1 - protect) + input[i] * protect);
    const result = rgbHueSat(out[0], out[1], out[2]);
    // Golden practical lights can put otherwise plausible skin just past the
    // warm band. Pull only these protected colours back to its edge, and cap
    // chroma; no arena can turn a portrait green, violet, or acid-yellow.
    const safeHue = result.hue > 180 ? 16 : Math.min(34, Math.max(16, result.hue));
    out = hsvToRgb(safeHue, Math.min(0.64, Math.max(0.22, result.saturation)), result.value);
  }
  return out;
}

export function isLutType(type) {
  return Object.hasOwn(LUT_GRADES, String(type || '').toUpperCase());
}

export function getLutTexture(type) {
  const key = String(type || '').toUpperCase();
  if (!isLutType(key)) throw new Error(`Unknown LUT grade: ${type}`);
  if (cache.has(key)) return cache.get(key);

  const grade = LUT_GRADES[key];
  const data = new Uint8Array(LUT_SIZE ** 3 * 4);
  let offset = 0;
  for (let bz = 0; bz < LUT_SIZE; bz++) {
    for (let gy = 0; gy < LUT_SIZE; gy++) {
      for (let rx = 0; rx < LUT_SIZE; rx++) {
        const out = gradeRgb([rx / (LUT_SIZE - 1), gy / (LUT_SIZE - 1), bz / (LUT_SIZE - 1)], grade);
        data[offset++] = Math.round(Math.min(1, Math.max(0, out[0])) * 255);
        data[offset++] = Math.round(Math.min(1, Math.max(0, out[1])) * 255);
        data[offset++] = Math.round(Math.min(1, Math.max(0, out[2])) * 255);
        data[offset++] = 255;
      }
    }
  }

  const texture = new THREE.Data3DTexture(data, LUT_SIZE, LUT_SIZE, LUT_SIZE);
  texture.name = `arena-grade-${key}`;
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = texture.wrapT = texture.wrapR = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
  cache.set(key, texture);
  return texture;
}
